import { Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCompanionName } from '@/hooks/useCompanionName';

interface FirstRunCardProps {
  variant?: 'dashboard' | 'tasks';
}

// The copy per variant is a key pair (#2109 R3 L1). The companion name is a
// per-family persona, so it is interpolated at render, never frozen (#639).
const COPY_KEY = {
  dashboard: { title: 'firstRun.dashboard.title', body: 'firstRun.dashboard.body' },
  tasks: { title: 'firstRun.tasks.title', body: 'firstRun.tasks.body' },
} as const satisfies Record<NonNullable<FirstRunCardProps['variant']>, { title: string; body: string }>;

export function FirstRunCard({ variant = 'dashboard' }: FirstRunCardProps) {
  const navigate = useNavigate();
  const companionName = useCompanionName();
  const { t } = useTranslation('onboarding');
  const keys = COPY_KEY[variant];
  const copy = { title: t(keys.title), body: t(keys.body, { companion: companionName }) };

  return (
    <Card className="border-muted">
      <CardContent className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="rounded-full bg-primary/10 p-3 mb-5">
          <Sparkles className="h-6 w-6 text-primary" />
        </div>
        <p className="text-lg font-medium mb-2">{copy.title}</p>
        <p className="text-body text-stevens-ink-3 max-w-md mb-6">{copy.body}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/chat')}>
            {t('firstRun.chat', { companion: companionName })}
          </Button>
          {/* /profile was a phantom — no route ever declared it (design-grammar
              §4.2). The profile itself is a dialog off the sidebar, so the
              URL-addressable profile surface is Settings' Account tab. */}
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings?tab=account')}>
            {t('firstRun.updateProfile')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
